export {
  DECIMAL_PRECISION,
  normalizeDecimalInput,
  formatDecimal,
  roundDecimal,
} from "@/lib/decimalUtils";

function parseFloatOrNull(value: string): number | null {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function sumExpressionParts(value: string): number[] {
  const parts: number[] = [];
  for (const segment of value.split("+")) {
    const trimmed = segment.trim();
    if (trimmed === "") continue;
    const parsed = parseFloatOrNull(trimmed);
    if (parsed !== null) parts.push(parsed);
  }
  return parts;
}

/**
 * Parse a sum expression like "10+5.5" into the raw numeric sum.
 * Caller is responsible for rounding (e.g. via roundDecimal) before persisting.
 */
export function parseSumExpression(value: string | number): number {
  if (typeof value === "number") return value;
  if (!value || value === "") return 0;
  if (value.includes("+")) {
    return sumExpressionParts(value).reduce((acc, n) => acc + n, 0);
  }
  return parseFloatOrNull(value) ?? 0;
}

/**
 * Nullable variant of parseSumExpression. Returns null for empty/invalid input,
 * sums valid parts of an expression (ignoring invalid ones), and returns null
 * only when no part is parseable.
 */
export function parseSumExpressionNullable(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return value;
  if (!value || value.trim() === "") return null;
  if (value.includes("+")) {
    const parts = sumExpressionParts(value);
    return parts.length === 0 ? null : parts.reduce((acc, n) => acc + n, 0);
  }
  return parseFloatOrNull(value);
}
